import { SlugPicker } from './SlugPicker';
import type { SlugDef } from '../../lib/broadcastSlugs';
import { useTextInsertion } from './useTextInsertion';

// Stage 6A §5.2 — markdown body editor with a formatting toolbar + slug picker.
// Plain textarea underneath (the body IS markdown); the toolbar just inserts
// markdown syntax around the selection.

type MarkdownEditorProps = {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  rows?: number;
  // Slug catalogue (defaults to broadcast SLUG_DEFS); surveys pass SURVEY_SLUG_DEFS.
  defs?: SlugDef[];
};

export function MarkdownEditor({ value, onChange, placeholder, rows = 12, defs }: MarkdownEditorProps) {
  const { ref, insertAtCursor, wrapSelection, prefixLines, replaceSelection } =
    useTextInsertion<HTMLTextAreaElement>(value, onChange);

  const insertLink = () =>
    replaceSelection((sel) => {
      const label = sel || 'link text';
      const prefix = `[${label}](`;
      return {
        text: `${prefix}https://)`,
        // select the URL placeholder so the AM types straight over it
        selectStart: prefix.length,
        selectEnd: prefix.length + 'https://'.length,
      };
    });

  return (
    <div className="rounded border border-slate-300">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 p-1">
        <ToolbarButton title="Bold" onClick={() => wrapSelection('**', '**')} className="font-bold">
          B
        </ToolbarButton>
        <ToolbarButton title="Italic" onClick={() => wrapSelection('*', '*')} className="italic">
          I
        </ToolbarButton>
        <ToolbarButton title="Bullet list" onClick={() => prefixLines(() => '- ')}>
          • List
        </ToolbarButton>
        <ToolbarButton title="Numbered list" onClick={() => prefixLines((i) => `${i + 1}. `)}>
          1. List
        </ToolbarButton>
        <ToolbarButton title="Insert link" onClick={insertLink}>
          Link
        </ToolbarButton>
        <div className="ml-auto">
          <SlugPicker onInsert={insertAtCursor} defs={defs} />
        </div>
      </div>
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full resize-y rounded-b px-3 py-2 font-mono text-sm focus:outline-none"
      />
    </div>
  );
}

function ToolbarButton({
  children,
  title,
  onClick,
  className = '',
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`rounded px-2 py-1 text-sm text-slate-700 hover:bg-slate-100 ${className}`}
    >
      {children}
    </button>
  );
}
